import React from 'react';
import { Link } from 'react-router-dom';
import { Plus, ArrowUpRight } from 'lucide-react';
import { useCart } from '../../context/CartContext';

/**
 * ProductCard - "Editorial Cover" style
 * Completely different structure:
 *   - Square 1:1 aspect (vs previous 4:5 portrait)
 *   - Info OVERLAID on the image (no info area below)
 *   - Big serif number prefix top-left (00-12 catalog index)
 *   - Circular + button top-right (vs previous bottom pill CTA)
 *   - Name + price stacked at bottom-left over gradient overlay
 *   - Handwritten Caveat tag for badge
 */
const ProductCard = ({ product, index = 0, variant = 'default' }) => {
  const { addToCart } = useCart();

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
  };

  const number = String(index + 1).padStart(2, '0');

  // Compact list-row variant (used on shop list view)
  if (variant === 'row') {
    return (
      <Link
        to={`/product/${product.slug}`}
        className="group flex items-center gap-5 lg:gap-7 py-6 border-b border-warmBrown-900/10 hover:bg-cream-50/40 -mx-3 px-3 lg:px-5 transition-colors"
      >
        <span className="font-display text-2xl lg:text-3xl text-warmBrown-300 group-hover:text-rose-500 transition-colors w-12 lg:w-16 flex-shrink-0">
          {number}
        </span>
        <div className="w-20 h-20 lg:w-28 lg:h-28 rounded-full overflow-hidden bg-cream-100 flex-shrink-0">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        </div>
        <div className="flex-1 min-w-0">
          {product.badge && (
            <span className="font-hand text-base text-rose-600 -mb-1 block">{product.badge}</span>
          )}
          <h3 className="font-display text-xl lg:text-2xl text-warmBrown-950 group-hover:text-rose-600 transition-colors line-clamp-1">{product.name}</h3>
          <p className="text-warmBrown-500 text-sm line-clamp-1 mt-1 hidden sm:block">{product.description}</p>
        </div>
        <p className="font-display text-xl lg:text-3xl text-warmBrown-950 whitespace-nowrap">${product.price.toFixed(2)}</p>
        <button
          onClick={handleAddToCart}
          className="flex-shrink-0 w-11 h-11 lg:w-14 lg:h-14 rounded-full bg-warmBrown-900 hover:bg-rose-600 text-cream-50 flex items-center justify-center transition-colors"
          aria-label={`Add ${product.name} to cart`}
        >
          <Plus className="w-4 h-4 lg:w-5 lg:h-5" />
        </button>
      </Link>
    );
  }

  // Default: editorial cover overlay card
  return (
    <Link
      to={`/product/${product.slug}`}
      className="group relative block aspect-square overflow-hidden rounded-[8px] focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
    >
      {/* Image fills card */}
      <img
        src={product.image}
        alt={product.name}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover bg-cream-100 transition-transform duration-700 ease-out group-hover:scale-105"
      />

      {/* Always-on bottom gradient for legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-warmBrown-950/85 via-warmBrown-950/20 to-transparent" />

      {/* Top decorative row: number + handwritten badge */}
      <div className="absolute top-4 left-5 right-5 flex items-start justify-between">
        <div className="flex items-baseline gap-2 text-cream-50">
          <span className="font-display text-[2.5rem] sm:text-5xl leading-none drop-shadow-md">{number}</span>
          <span className="text-[10px] uppercase tracking-[0.18em] opacity-80">/ in stock</span>
        </div>

        {/* Circular plus button - completely different from old pill CTA */}
        <button
          onClick={handleAddToCart}
          className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-cream-50 hover:bg-rose-500 hover:text-cream-50 text-warmBrown-950 flex items-center justify-center transition-all shadow-elevated group-hover:rotate-90"
          aria-label={`Add ${product.name} to cart`}
        >
          <Plus className="w-5 h-5" strokeWidth={2.4} />
        </button>
      </div>

      {/* Handwritten badge tag */}
      {product.badge && (
        <span className="absolute top-20 left-5 font-hand text-2xl text-rose-300 -rotate-6 drop-shadow-md">
          {product.badge}
        </span>
      )}

      {/* Bottom info overlaid on image */}
      <div className="absolute inset-x-0 bottom-0 p-5 text-cream-50">
        <div className="flex items-end justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-xl sm:text-2xl leading-tight line-clamp-2 group-hover:text-rose-200 transition-colors">
              {product.name}
            </h3>
            <div className="mt-2 flex items-center gap-2 text-cream-200/85 text-[11px] uppercase tracking-[0.16em] opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <span>View product</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </div>
          </div>
          <p className="font-display text-3xl sm:text-4xl text-cream-50 whitespace-nowrap leading-none">
            ${product.price.toFixed(0)}
            <span className="text-base align-top">.{product.price.toFixed(2).split('.')[1]}</span>
          </p>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
